import { supabase } from "@/integrations/supabase/client";
import { calculateNextUpdateDue, formatProgramDataForAI, cleanTierData } from "@/utils/program-utils";
import { Client } from "@/components/programs/ClientSelection";
import { TierFormData } from "@/contexts/program-creation/types";

/**
 * Save a program to the database
 */
export const saveProgram = async (
  programData: any,
  tierData: TierFormData,
  preferencesData: any,
  selectedClient: Client,
  userId: string
) => {
  // Format data for AI model
  const completeData = formatProgramDataForAI(
    programData,
    tierData,
    preferencesData,
    userId,
    selectedClient
  );

  // Clean tierData
  const cleanedTierData = cleanTierData(tierData);

  // Insert into the programs table with structured data
  const { data, error } = await supabase
    .from('programs')
    .insert({
      coach_id: userId,
      client_id: selectedClient.id,
      name: programData.programName,
      description: programData.description || '',
      tier: programData.tier,
      start_date: programData.startDate || new Date().toISOString().split('T')[0],
      duration: programData.duration || "4",
      frequency: parseInt(programData.daysPerWeek) || 3,
      health_info: programData.medicalConditions === "yes" 
        ? programData.medicalDetails 
        : (programData.injuries || "None"),
      program_data: completeData,
      tier_questions: cleanedTierData,
      next_update_due: calculateNextUpdateDue(programData.duration || "4")
    })
    .select();

  if (error) {
    throw new Error(`Failed to create program: ${error.message}`);
  }

  // Create an empty program_data entry if program was created successfully
  if (data && data.length > 0) {
    const programId = data[0].program_id;
    
    const { error: programDataError } = await supabase
      .from('program_data')
      .insert({
        program_id: programId,
        data: {} // Empty JSON object for later input
      });
    
    if (programDataError) {
      console.error("Error creating program_data entry:", programDataError);
      // Not throwing error as the program was still created successfully
    }
  }

  return data;
};

/**
 * Create program activity record
 */
export const createProgramActivity = async (userId: string, programId: string, programName: string) => {
  const { error } = await supabase.rpc(
    'create_program_activity',
    {
      user_id_input: userId,
      program_id_input: programId,
      program_name_input: programName
    }
  );
    
  if (error) {
    console.error("Error creating program activity:", error);
    // Not throwing error as this is non-critical
  }

  return !error;
};
